import { FC, HTMLAttributes, useEffect, useMemo, useState, useRef, CSSProperties } from "react";
import styled from "styled-components";
import { animated, useTransition, easings } from "@react-spring/web";
import { parseNumber } from "utils/number";
import { getAvatar } from "utils/get-avatar";
import { useStore } from "utils/store";
import { AnimButton, Center } from "utils/constants";
import { usePlayer, useSummary, useTournamentDetails, useCurrency } from "hooks";
import { ApngPlayer } from "components/ApngPlayer";
import SparkImg from "assets/images/sparks.png";
import { useScale } from "hooks/size";
import { useSoundStore } from "utils/sound-store";
import { Sounds} from "types/sound";
import { useClickAnyWhere, useDebounceCallback, useTimeout } from "usehooks-ts";
import { useSkinnedAssets } from "hooks/skin";
import { useTranslation, Trans } from "react-i18next";

const Card = styled(animated.div)<{ $hidden: boolean }>`
  position: absolute;
  width: 268px;
  height: 420px;
  top: 50%;
  left: 50%;
  translate: -50% -50%;
  z-index: 20;
  background-image: url(images/scratch-card/card-bg.svg);
  background-size: 266px 419px;
  background-repeat: no-repeat;
  background-position: center center;
  box-sizing: border-box;
  display: ${props => props.$hidden ? 'none' : 'flex'};
  flex-direction: column;
`;

const Top = styled.div`
  display: flex;
  width: 100%;
`;

const Note = styled(Center)`
  position: relative;
  margin-top: 6px;
  margin-bottom: 3px;
  margin-left: 6px;
  width: 100%;
  z-index: 0;
  flex-direction: column;
  font-size: 14.8px;
  font-weight: 600;
  line-height: 1.07;
  letter-spacing: 0.57px;
  text-align: center;
  color: #0f0;
`;

const GradText = styled.p`
  background-image: linear-gradient(to bottom, #cf9b5e 0%, #efe090 50%, #cf9b5e 100%);
  line-height: 0.85;
  letter-spacing: normal;
  text-align: center;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
`;

const Cash = styled.div`
  position: relative;
  font-size: 34.3px;
  line-height: 26.1px;
  font-weight: 700;
  margin-top: 21px;
`;

const Symbol = styled.span`
  font-size: 27.4px;
  font-weight: 600;
`;

const Entry = styled.div`
  font-size: 12px;
  font-weight: 500;
  margin-top: 13px;
`;

const Label = styled.p`
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.46px;
  color: #d29b75;
  text-align: center;
  margin: 0;
  line-height: 16.3px;
`;

const EntryId = styled.div`
  font-size: 13px;
  line-height: 9px;
  font-weight: 500;
  color: #ffd674;
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 13px;
  padding-left: 12px;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 17px;
  padding-right: 19px;
  width: 100%;
`;

const Bounty = styled.div<{ $firstRun?: boolean }>`
  position: relative;
  width: 63px;
  height: 19px;
  border-radius: 17px;
  background-color: #0f0;
  font-size: 13px;
  font-weight: bold;
  line-height: 1.6;
  letter-spacing: normal;
  text-align: center;
  color: rgba(0, 0, 0, 0.75);
  opacity: ${props => props.$firstRun ? 0 : 1};
  margin-top: -23px;
  margin-bottom: 6px;
`;

const Prizes = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 7px 8px;
  margin-top: 9px;
`;

const ButtonLabel = styled.div`
  text-shadow: 0 1px 0 #eddd8e;
  font-size: 17.7px;
  font-weight: 600;
  line-height: 0.85;
  color: #3c3531;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .is-scratched & {
    display: none;
  }
`;

const Button = styled(AnimButton)<{ $active: boolean, $animate?: boolean }>`
  position: relative;
  width: 106px;
  height: 47px;
  border: none;
  border-radius: 7px;
  overflow: hidden;
  font-family: Teko;
  font-weight: 600;
  letter-spacing: normal;
  text-align: center;
  color: #3c3531;
  background-image: linear-gradient(23deg, #ffefcd -4%, #fff1c4 47%, #fff7ce 100%);
  pointer-events: ${props => props.$active ? 'auto' : 'none'};
  padding: 0;
  background-image: url("images/scratch-card/scratch-gold.svg");

  &.silver {
    background-image: url("images/scratch-card/scratch-silver.svg");

    & > ${ButtonLabel} {
        text-shadow: 0 1px 0 rgba(255, 255, 255, 0.61);
    }
  }
`;

const ButtonResult = styled.div`
  display: flex; 
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  transition: opacity 100ms;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    background-image: url("images/scratch-card/scratched-gold.svg");
    width: 0%;
    height: 100%;
    transition: width 1000ms;

    .silver & {
      background-image: url("images/scratch-card/scratched-silver.svg");
    }
  }

  .is-scratched & {
    opacity: 100%;

    &::before {
      width: 100%;
    }
  }
`;

const ButtonResultLabel = styled.div`
  font-size: 13.3px;
  font-weight: 600;
  line-height: 1.13;
  color: #4a4003;
  position: relative;
  transition: opacity 1000ms;
  opacity: 0;

  .is-scratched & {
    opacity: 1;
  }
`;

const ButtonValue = styled.div<{ $total?: boolean }>`
  font-size: 17.7px;
  font-weight: 600;
  line-height: 0.85;
  position: relative;
  transition: opacity 1000ms;
  opacity: 0;
  ${({ $total }) => $total ? 'color: #00c300' : 'color: #ff7100' };

  .is-scratched & {
    opacity: 1;
  }
`;

const WatchHighlights = styled.div`
  background-image: url(images/scratch-card/watch-highlights.svg);
  width: 109px;
  height: 57px;
  font-size: 21.708px;
  font-weight: 400;
  letter-spacing: 0.965px;
  line-height: 0.8;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding-top: 2px;
  margin-top: 4px;
  cursor: pointer;
`;

const Avatar = styled.img`
  width: 66px;
  height: 132px;
  object-fit: contain;
  object-position: bottom center;
  position: relative;
`;

const PlayerId = styled.div`
  font-size: 13px;
  font-weight: 500;
  line-height: 0.6;
  text-align: center;
  color: #ffd674;
`;

const SquareButton = styled.button`
  position: relative;
  cursor: pointer;
  width: 64px;
  height: 64px;
  padding: 0;
  background-color: transparent;
  border: none;
  padding: 10px;
  margin-top: -8px;

  &.left {
    margin-right: -6px;
  }

  &.right {
    margin-left: -6px;
  }

  span {
    display: block;
    margin-top: -4px;
    position: relative;
    background-image: linear-gradient(to bottom, #000 51%, rgba(87, 84, 84, 0.82) 72%, #000 96%);
    font-family: Teko;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    text-align: center;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url(images/scratch-card/square-gold.svg);
  }
`;

const ScratchAll = styled(SquareButton)<{ $show: boolean }>`
  display: ${props => props.$show ? 'block' : 'none'};
  span {
    font-size: 11px;
  }

  &::before {
    background-image: url(images/scratch-card/square-gold.svg);
  }
`;

const Watch = styled(SquareButton)`
  span {
    font-size: 13px;
  }

  &::before {
    background-image: url(images/scratch-card/square-green.svg);
  }
`;

const Lobby = styled(SquareButton)`
  span {
    font-size: 15px;
  }

  &::before {
    background-image: url(images/scratch-card/square-silver.svg);
  }

  position: absolute;
  opacity: 0;
  pointer-events: none;

  &.is-visible {
    position: relative;
    opacity: 1;
    pointer-events: all;
  }
`;


const Play = styled(SquareButton)`
  span {
    font-size: 15px;
  }

  &::before {
    background-image: url(images/scratch-card/square-gold.svg);
  }
`;

const Logo2 = styled.img`
  width: 109px;
`;

export type ScratchCardProps = HTMLAttributes<HTMLDivElement> & {
  show: boolean;
  playAgain: boolean;
  onWatch: (isFirstRun: boolean) => void;
  onLobby: () => void;
  onPlay: () => void;
  isEliminated?: boolean;
};

export const ScratchCard: FC<ScratchCardProps> = ({ show, playAgain, onWatch, onLobby, onPlay, isEliminated, ...props }) => {
  const playerId = useStore(state => state.playerId);
  const { handsPlayed, firstFlush, secondFlush, bountiesWonCount, totalBountyWon, totalJackpotWon, totalWinnings } = useSummary(true);
  const [boxes, setBoxes] = useState([false, false, false, false, false, false, false]);
  const [startTimer, setStartTimer] = useState(false);
  const expiredRef = useRef(false);
  const { parseCurrency } = useCurrency();
  const bountyRef = useRef(0);
  const { bounty } = usePlayer();
  const id = useStore(state => state.id);
  const lastRef = useRef<number[]>([]);
  const isFirstRun = useRef(true);
  const { scale } = useScale();
  const play = useSoundStore(store => store.play);
  const stop = useSoundStore(store => store.stop);
  const cardRef = useRef<HTMLDivElement>(null);
  const shownRef = useRef(false);
  const { buyIn } = useTournamentDetails();
  const [currentBuyIn, setCurrentBuyIn] = useState(buyIn);
  const showRegConfirm = useStore(state => state.showRegConfirm);
  const { logoHorizontal, logoMobile } = useSkinnedAssets();
  const { t } = useTranslation();

  useEffect(() => {
    if (!currentBuyIn?.buyIn && buyIn) {
      setCurrentBuyIn(buyIn);
    }
  }, [buyIn]);

  const debStop = useDebounceCallback((sound: Sounds) => {
    stop(sound);
  }, 1000);

  useEffect(() => {
    if(!bountyRef.current) bountyRef.current = bounty;
  }, [bounty]);

  useEffect(() => {
    if(playAgain) setBoxes(a => a.map(() => true));
  }, [playAgain]);

  const scratchedAll = useMemo(() => {
    return boxes.every(value => value);
  }, [boxes]);

  const showRegConfirmRef = useRef(showRegConfirm);

  useClickAnyWhere((e: MouseEvent) => {
    if (isEliminated || showRegConfirm || showRegConfirmRef.current) {
      return;
    }

    const target = e.target as Node;

    if(!cardRef.current?.contains(target) && shownRef.current && !isFirstRun.current){
      onWatch(isFirstRun.current);
    }
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      showRegConfirmRef.current = showRegConfirm;
    }, 100);

    return () => {
      clearTimeout(timeout);
    }
  }, [showRegConfirm]);

  const handleScratchAll = () => {
    play(Sounds.Scratch);
    debStop(Sounds.Scratch);
    lastRef.current = [];
    setBoxes(a => a.map((value, index) => {
      if(!value) lastRef.current.push(index);
      return true;
    }));
  };

  useTimeout(() => {
    expiredRef.current = true;
    setStartTimer(false);
  },!startTimer || boxes.filter((value) => value).length > 1 ? null : 9000);

  useEffect(() => {
    if(!startTimer && expiredRef.current){
      expiredRef.current = false;
      onWatch(isFirstRun.current);
    }
  }, [startTimer, onWatch]);

  const handleClickDown = () => {
    play(Sounds.SpinStop);
  };

  const handleClick = (n: number) => {
    play(Sounds.Scratch);
    debStop(Sounds.Scratch);
    lastRef.current = [n];
    setBoxes(a => a.map((value, index) => index === n ? true : value));
  };

  useEffect(() => {
    if(show){
      if(!isFirstRun.current) play(Sounds.PopupDrop);
    }else{
      stop(Sounds.Scratch);
    }
  }, [play, show, stop]);

  const transitions = useTransition(show, {
    from: { 
      transform: `translateY(${isFirstRun.current ? 100 : -100}vh) scale(${scale})`, 
      opacity: 1
    },
    enter: { 
      transform: `translateY(0) scale(${scale})`, 
      opacity: 1
    }, 
    leave: { 
      transform: `translateY(0) scale(${scale})`, 
      opacity: 0,
      onRest: (_result, _spring, show: boolean) => {
        if(show){
          if(playAgain) 
            handleScratchAll();
          else
            lastRef.current = [];
          isFirstRun.current = false;
          shownRef.current = false;
        }else{
          stop(Sounds.Dispense);
          if (isFirstRun.current) {
            setStartTimer(true);
          }
          shownRef.current = true;
        }
      }
    },
    delay: isFirstRun.current && show ? 250 : 0,
    config: {
      duration: 250,
      easing: easings.easeOutSine
    }
  });

  const sparkStyle: CSSProperties = {
    position: 'absolute',
    pointerEvents: 'none',
    width: '79px',
    height: '158px',
    display: isFirstRun.current ? 'none' : 'block',
  }

  const getAnimate = (n: number) => {
    return !boxes[n] || lastRef.current.includes(n);
  };

  const handleWatch = () => {
    setStartTimer(false);
    onWatch(isFirstRun.current);
  };

  const handleDown = () => {
    play(Sounds.MouseClick);
  };

  return transitions((style, show) => show &&
    <Card ref={cardRef} style={style} data-testid="scratch-card" $hidden={showRegConfirm}>
      <Top>
        <Left>
          {isEliminated ? (
            <img src={logoMobile} alt="" width={114} height={79} />
          ) :
          (<Note>
            {!scratchedAll ? t('SCRATCH ANY OR...') : <Logo2 src={logoHorizontal} alt="" />}
            <WatchHighlights onMouseDown={handleDown} onClick={handleWatch} data-testid="scratch-card-watch-highlights">
              {t('WATCH THE HIGHLIGHTS')}
            </WatchHighlights>
          </Note>)
          }
          <Cash style={{ marginTop: scratchedAll ? '10px' : '21px'}}>
            <GradText data-testid="scratch-card-buy-in">
              <Symbol>$</Symbol>{parseNumber(currentBuyIn.buyIn)}
            </GradText>
            </Cash>
          <Entry data-testid="scratch-card-entry-id">
            <Label>{t('ENTRY #')}</Label>
            <EntryId>{id}</EntryId>
          </Entry>
        </Left>
        <Right>
          <Avatar src={getAvatar(parseInt(playerId.slice(-1)))} alt="avatar" />
          <Bounty $firstRun={isFirstRun.current && !scratchedAll}>{parseCurrency(bountyRef.current)}</Bounty>
          <Label>{t('PLAYER #')}</Label>
          <PlayerId data-testid="scratch-card-player-id">{playerId}</PlayerId>
        </Right>
      </Top>
      <Prizes>
        <Button $active={!boxes[0]} $animate={getAnimate(0)} onMouseDown={handleClickDown} onClick={() => handleClick(0)} className={boxes[0] ? 'is-scratched' : ''}>
          <ButtonLabel>{t('Hands Played')}</ButtonLabel>
          <ButtonResult>
            <ButtonResultLabel>{t('Hands Played')}</ButtonResultLabel>
            <ButtonValue>{parseNumber(handsPlayed || 0)}</ButtonValue>
          </ButtonResult>
        </Button>
        <Button $active={!boxes[1]} $animate={getAnimate(1)} onMouseDown={handleClickDown} onClick={() => handleClick(1)} className={boxes[1] ? 'is-scratched' : ''}>
          <ButtonLabel>{t('First Flush')}</ButtonLabel>
          <ButtonResult>
            <ButtonResultLabel>{t('First Flush')}</ButtonResultLabel>
            <ButtonValue>{firstFlush || t('N/A')}</ButtonValue>
          </ButtonResult>
        </Button>
        <Button $active={!boxes[2]} $animate={getAnimate(2)} onMouseDown={handleClickDown} onClick={() => handleClick(2)} className={boxes[2] ? 'is-scratched' : ''}>
          <ButtonLabel>{t('# of Bounties Won')}</ButtonLabel>
          <ButtonResult>
            <ButtonResultLabel>{t('Bounties Won')}</ButtonResultLabel>
            <ButtonValue>{parseNumber(bountiesWonCount || 0)}</ButtonValue>
          </ButtonResult>
        </Button>
        <Button $active={!boxes[3]} $animate={getAnimate(3)} onMouseDown={handleClickDown} onClick={() => handleClick(3)} className={boxes[3] ? 'is-scratched' : ''}>
          <ButtonLabel>{t('Second Flush')}</ButtonLabel>
          <ButtonResult>
            <ButtonResultLabel>{t('Second Flush')}</ButtonResultLabel>
            <ButtonValue>{secondFlush || t('N/A')}</ButtonValue>
          </ButtonResult>
        </Button>
        <Button $active={!boxes[4]} $animate={getAnimate(4)} onMouseDown={handleClickDown} onClick={() => handleClick(4)} className={boxes[4] ? 'is-scratched silver' : 'silver'}>
          <ButtonLabel>{t('Bounty Winnings')}</ButtonLabel>
          <ButtonResult>
            <ButtonResultLabel>{t('Bounty Winnings')}</ButtonResultLabel>
            <ButtonValue $total={true}>{parseCurrency(totalBountyWon)}</ButtonValue>
          </ButtonResult>
        </Button>
        <Button $active={!boxes[5]} $animate={getAnimate(5)} onMouseDown={handleClickDown} onClick={() => handleClick(5)} className={boxes[5] ? 'is-scratched silver' : 'silver'}>
          <ButtonLabel>{t('Jackpot Winnings')}</ButtonLabel>
          <ButtonResult>
            <ButtonResultLabel>{t('Jackpot Winnings')}</ButtonResultLabel>
            <ButtonValue $total={true}>{parseCurrency(totalJackpotWon)}</ButtonValue>
          </ButtonResult>
        </Button>
        <ScratchAll className="left" onMouseDown={handleDown} onClick={handleScratchAll} $show={!scratchedAll}><span>{t('SCRATCH ALL')}</span></ScratchAll>
        <Lobby className={`left ${scratchedAll ? 'is-visible' : ''}`} onMouseDown={handleDown} onClick={onLobby}><span>{t('LOBBY')}</span></Lobby>
        <Button $active={!boxes[6]} $animate={getAnimate(6)} onMouseDown={handleClickDown} onClick={() => handleClick(6)} className={boxes[6] ? 'is-scratched silver' : 'silver'}>
          <ButtonLabel><Trans i18nKey='TOTAL WINNINGS' components={[<br />]} /></ButtonLabel>
          <ButtonResult>
            <ButtonResultLabel><Trans i18nKey='TOTAL WINNINGS' components={[]} /></ButtonResultLabel>
            <ButtonValue $total={true}>{parseCurrency(totalWinnings)}</ButtonValue>
          </ButtonResult>
        </Button>
        {!scratchedAll ?
          <Watch className="right" onMouseDown={handleDown} onClick={handleWatch}><span>{t('WATCH')}</span></Watch>
        :
          <Play className="right" onMouseDown={handleDown} onClick={onPlay}><span>{<Trans i18nKey='PLAY AGAIN' components={[<br />]} />}</span></Play>}
      </Prizes>
      <ApngPlayer
        delay={0.1}
        rate={3}
        src={SparkImg}
        style={{
          ...sparkStyle,
          left: '-39px',
          top: '50px'
        }}
      />
      <ApngPlayer
        delay={0.1}
        rate={2.25}
        src={SparkImg}
        style={{
          ...sparkStyle,
          left: '-37px',
          top: '320px'
        }}
      />
      <ApngPlayer
        delay={0.1}
        rate={2.75}
        src={SparkImg}
        style={{
          ...sparkStyle,
          left: '229px',
          top: '20px'
        }}
      />
      <ApngPlayer
        delay={0.1}
        rate={2.5}
        src={SparkImg}
        style={{
          ...sparkStyle,
          left: '230px',
          top: '220px'
        }}
      />
    </Card>
  );
};
